import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { closeMobileViewDialog } from "../../redux/reducers/mobileViewDialog";
import { ArrowBack } from "@material-ui/icons";
import { Box, Button, TextField, Typography, Grid } from "@mui/material";
import { ReactComponent as MobileViewModalLogo } from "@assets/images/MobileViewModalLogo.svg";
import { useNavigate } from "react-router-dom";
import { useDiscountPromoCode } from "../../services/mutations";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";

const DialogContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 0;
  overflow-y: auto;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  background-color: white;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.05);
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #aa7cfb;
`;

const Header = styled.h2`
  font-family: "DM Sans", sans-serif;
  font-weight: 700;
  font-size: 17px;
  line-height: 20px;
  color: #0f0f0f;
  margin-bottom: 0px;
`;

const Paragraph = styled.p`
  font-family: "DM Sans", sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: #123051;
  margin-bottom: 0px;
`;

const StyledSpan = styled.span`
  font-family: DM Sans;
  font-weight: 800;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0%;
`;
const PurpleStyledSpan = styled.span`
  font-family: DM Sans;
  font-weight: 800;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #9b5af8;
`;

const MobileViewDialog = ({ wheelData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, title } = useSelector((state) => state.mobileViewDialog);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  // Use the discount promo code mutation
  const discountPromoCode = useDiscountPromoCode();

  // Check form validity whenever fields change
  useEffect(() => {
    setIsFormValid(
      firstName.trim() !== "" &&
        lastName.trim() !== "" &&
        email.trim() !== "" &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) // Basic email validation
    );
  }, [firstName, lastName, email]);

  if (!isOpen) return null;

  const handleClose = () => {
    dispatch(closeMobileViewDialog());
  };

  const handleSubmit = () => {
    // Create full name from first and last name
    const name = `${firstName} ${lastName}`;

    // Call the discount promo code API
    discountPromoCode.mutate(
      {
        email,
        name,
        referral_code: !isEmpty(wheelData?.wheel_referrer)
          ? wheelData.wheel_referrer?.referral_code
          : null,
      },
      {
        onSuccess: (response) => {
          toast.success("Success! Check your email for your discount code.");
          setEmail("");
          setFirstName("");
          setLastName("");
          handleClose();
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message ||
              "Unable to process your request. Please try again."
          );
        },
      }
    );
  };

  const handleSignIn = () => {
    navigate("/login");
    handleClose();
  };

  // Common text field styling
  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "8px",
      "& fieldset": {
        borderColor: "#CED4DA",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#CED4DA",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#9B5AF8",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 16px",
      backgroundColor: "white",
      fontFamily: "DM Sans",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "24px",
      letterSpacing: "0px",
      color: "#AAABAD",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(16px, 12px) scale(1)",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(16px, -6px) scale(0.75)",
    },
  };

  return (
    <DialogContainer>
      <ContentContainer>
        <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <BackButton onClick={handleClose}>
            <ArrowBack />
          </BackButton>

          <Typography
            sx={{
              fontFamily: "DM Sans",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "20px",
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: "#123051",
            }}
          >
            Back
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            marginBottom: "5px",
          }}
        >
          <MobileViewModalLogo style={{ height: "40px", width: "40px" }} />
          <Header>{title}</Header>
        </Box>

        <Paragraph>
          Wheels are a blast to explore on mobile — but get ready, the full
          iYuga mobile app is rolling out mid-Q4 2025!
        </Paragraph>

        <Paragraph>
          Want to unlock the full power of iYuga right now? We’ve got something
          special just for you…
        </Paragraph>

        <Paragraph>
          <StyledSpan>
            Sign up for the Essential Tier or above in the next 24 hours
          </StyledSpan>{" "}
          {"(monthly or annual) and get "}
          <StyledSpan>{"50 free AI tokens "}</StyledSpan>
          to help you spin up stunning Wheels faster than ever.
        </Paragraph>

        <Paragraph>
          Plus! <StyledSpan>If you choose an Annual Plan,</StyledSpan> enjoy a
          whopping <StyledSpan>50% off your first year! 🎉</StyledSpan>
        </Paragraph>

        <Paragraph>
          Just drop your info below and we’ll send your exclusive desktop
          sign-up code straight to your inbox. Ready, set... Wheel!
        </Paragraph>

        {/* Name fields in a grid */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="First Name"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
              fullWidth
              required
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Last Name"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Your last name"
              fullWidth
              required
              sx={textFieldSx}
            />
          </Grid>
        </Grid>

        {/* Email field */}
        <TextField
          label="Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          fullWidth
          required
          sx={textFieldSx}
        />
      </ContentContainer>

      <ButtonContainer>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!isFormValid || discountPromoCode.isLoading}
          sx={{
            height: 40,
            padding: "6px 12px",
            borderRadius: "4px",
            backgroundColor: "#9B5AF8",
            "&:hover": {
              backgroundColor: "#7f47d8",
            },
            "&:disabled": {
              backgroundColor: "#D1D1D1",
              color: "#888888",
            },
            textTransform: "none",
            fontFamily: "DM Sans",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: "0px",
            textAlign: "center",
            color: "white",
          }}
        >
          {discountPromoCode.isLoading ? "Submitting..." : "Submit"}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleSignIn}
          sx={{
            height: 40,
            padding: "6px 12px",
            borderRadius: "4px",
            backgroundColor: "white",
            borderWidth: "1px",
            borderColor: "#9B5AF8",
            "&:hover": {
              borderColor: "#7f47d8",
              backgroundColor: "white",
            },
            textTransform: "none",
            fontFamily: "DM Sans",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: "0px",
            textAlign: "center",
            color: "#9B5AF8",
          }}
        >
          or Sign In Here
        </Button>
      </ButtonContainer>
    </DialogContainer>
  );
};

export default MobileViewDialog;
